import React, { useState, useEffect } from 'react';
import axios from 'axios';
import path from 'path';
import './card.css';
import { useParams } from 'react-router-dom';
import { url } from "../../api";
import frame1 from "./assets/frame1.png";
import frame3 from "./assets/frame3.png";
import frappeSketo from "./assets/coffees/frappe-sketo.svg";
import frappeGala from "./assets/coffees/frappe-gala.svg";
import espresso from "./assets/coffees/espresso.svg";
import cappuccino from "./assets/coffees/cappuccino.svg";
import freddoEsp from "./assets/coffees/freddo-esp.svg";
import freddoCap from "./assets/coffees/freddo-cap.svg";
import greek from "./assets/coffees/greek-coffee.svg";
import filter from "./assets/coffees/filter.svg";
import latte from "./assets/coffees/latte.svg";
import icedLatte from "./assets/coffees/iced-latte.svg";
import matcha from "./assets/coffees/matcha-latte.svg";
import freddoMatcha from "./assets/coffees/freddo-matcha.svg";
import icedMatcha from "./assets/coffees/iced-matcha.svg";

import home from "./assets/map/home.png";

interface DataState {
    mapImgUrl: string;
    qrImgUrl?: string;

    rateColor: string;
    overallRating: number;
    partialRatings: string;

    shopName: string;
    coordinates: string;
    shopLocation: string;
    selfMade: boolean;

    coffee: string;
    coffeeImg: string;
    coffeeFormat: string;
}

const mapping: any = {
    'frappe-sketo': frappeSketo,
    'frappe-gala': frappeGala,
    'espresso': espresso,
    'cappuccino': cappuccino,
    'freddo-esp': freddoEsp,
    'freddo-cap': freddoCap,
    'greek-coffee': greek,
    'filter': filter,
    'latte': latte,
    'iced-latte': icedLatte,

    // new coffee types mapped to existing icons
    'americano': filter,       // americano shares filter icon
    'lungo': espresso,         // lungo shares espresso icon
    'macchiato': espresso,     // macchiato shares espresso icon
    'flat-white': cappuccino,  // flat-white shares cappuccino icon
    'mocha': latte,            // mocha shares latte icon
    'affogato': freddoEsp,     // affogato shares freddo espresso icon (cold dessert coffee)
    'freddo-espresso': freddoEsp,  // normalized name for consistency
    'freddo-cappuccino': freddoCap, // normalized name for consistency
    'matcha': matcha,
    'freddo-matcha': freddoMatcha,
    'iced-matcha': icedMatcha


};

// helper function to normalize coffee type strings
const normalizeCoffeeType = (type: string): string => {
    let normalizedType = type.toLowerCase()
        .replace(/\s+/g, '-')  // replace spaces with hyphens
        .replace(/[^a-z0-9-]/g, ''); // remove special characters

    // special handling for frappe variations
    if (normalizedType.startsWith('frappe')) {
        return 'frappe-gala'; // default to frappe-gala for simplicity
    }

    // handle freddo variations
    if (normalizedType === 'freddoespresso') return 'freddo-espresso';
    if (normalizedType === 'freddocappuccino') return 'freddo-cappuccino';

    // handle other possible variations in input
    const coffeeTypeMap: { [key: string]: string } = {
        'filter-coffee': 'filter',
        'greekcoffe': 'greek-coffee',
        'greekcoffee': 'greek-coffee',
        'flatwhite': 'flat-white',
        'icedlatte': 'iced-latte',
        // add any other variations that might be entered differently
    };

    return coffeeTypeMap[normalizedType] || normalizedType;
};

const CoffeeCard: React.FC = () => {
    const { param } = useParams<{ param: string }>();

    const [data, setData] = useState<DataState>({
        mapImgUrl: '',
        qrImgUrl: '',
        rateColor: '',
        overallRating: 0,
        partialRatings: '',
        shopName: '',
        coordinates: '',
        shopLocation: '',
        coffee: '',
        coffeeFormat: '',
        coffeeImg: '',
        selfMade: false
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch the data from API
                const { data: fetchedData } = await axios.get(`${url}/api/coffee-rate/coffee-card/${param}`);

                // Destructure API response
                const {
                    mapImgUrl,
                    qrImgUrl,
                    rateColor,
                    overallRating,
                    partialRatings,
                    shopName,
                    coordinates,
                    shopLocation,
                    coffee,
                    coffeeFormat,
                    coffeeType
                } = fetchedData;

                // Update state with formatted data
                setData({
                    mapImgUrl,
                    qrImgUrl,
                    rateColor,
                    overallRating,
                    partialRatings,
                    shopName,
                    coordinates,
                    shopLocation,
                    coffee,
                    coffeeFormat,
                    coffeeImg: mapping[normalizeCoffeeType(coffeeType)] || mapping['espresso'],
                    selfMade: mapImgUrl === "self-made"
                });



            } catch (error) {
                console.error("Failed to fetch data: ", error);
            }
        };

        fetchData();
    }, [param]);

    return (
        <div className="card">
            <img src={(
                data.mapImgUrl === "self-made" ? home : data.mapImgUrl
            )} alt="Map" className="map-img" />
            <div className="map-text-div">
                <div className="text map-text">{data.shopName}</div>
            </div>
            <div className="map-pin"> </div>
            <img src={frame1} alt="Frame" className="frame" />
            <div className="rate-color" style={{ backgroundColor: data.rateColor }}>
                <div className="rating">{data.overallRating}</div>
            </div>

            <div className="coffee-img-div">
                <img src={data.coffeeImg} alt="Coffee" className="coffee-img" />
            </div>

            <div className="rate-div">
                <div className="rate-bar" style={{ backgroundColor: data.rateColor, width: `${data.overallRating * 10}%` }}></div>
            </div>
            <img src={frame3} alt="Frame" className="frame" />
            <div className="shopname-div">
                <div className="text shopname">{(data.selfMade ?
                    "Home Made" : data.shopName
                )}</div>
            </div>
            <div className="coor-div">
                <div className="text coordinates">{data.coordinates}</div>
            </div>
            <div className="coffee-div">
                <div className="coffee-type">{data.coffee}</div>
                <div className="coffee-format">{data.coffeeFormat}</div>
            </div>
            <div className="city-div">
                <div className="coffee-text">{data.shopLocation}</div>
            </div>
            <div className="qr-div">
                <img src={data.qrImgUrl} alt="QR Code" className="qr-img" />
            </div>
            <div className="partial-div">
                <div className="partial-text">{data.partialRatings}</div>
            </div>
        </div>
    );
};

export default CoffeeCard;
